import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { PromoCodeRedemption } from "@shared/schema";
import {
  Coins,
  ArrowDownToLine,
  Users,
  Gift,
  Sparkles,
  ShieldCheck,
  PlusCircle,
  MinusCircle,
  Youtube,
} from "lucide-react";

interface TransactionItemProps {
  id: string;
  type: "earn" | "withdrawal" | "referral" | "promo" | "sponsorship_purchase" | "sponsorship_delete_refund" | "sponsored_ad_reward" | "bot_token_credit" | "clip_reward";
  description: string;
  amount: number;
  status?: string;
  createdAt: Date;
  relatedId?: string;
  redemption?: PromoCodeRedemption;
}

const TransactionItem = ({
  type,
  description,
  amount,
  status,
  createdAt,
  redemption,
}: TransactionItemProps) => {
  const getTypeColor = (type: string) => {
    switch (type) {
      case "earn":
        return "text-accent";
      case "withdrawal":
        return "text-primary";
      case "referral":
        return "text-secondary";
      case "promo":
        return "text-yellow-500";
      case "sponsorship_purchase":
        return "text-red-500";
      case "sponsorship_delete_refund":
        return "text-green-500";
      case "sponsored_ad_reward":
        return "text-blue-500";
      case "bot_token_credit":
        return "text-purple-500";
      case "clip_reward":
        return "text-pink-500"; // Add color for clip_reward
      default:
        return "text-foreground";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "earn":
        return <Coins className="h-5 w-5" />;
      case "withdrawal":
        return <ArrowDownToLine className="h-5 w-5" />;
      case "referral":
        return <Users className="h-5 w-5" />;
      case "promo":
        return <Gift className="h-5 w-5" />;
      case "sponsorship_purchase":
        return <MinusCircle className="h-5 w-5" />;
      case "sponsorship_delete_refund":
        return <PlusCircle className="h-5 w-5" />;
      case "sponsored_ad_reward":
        return <ShieldCheck className="h-5 w-5" />;
      case "bot_token_credit":
        return <Sparkles className="h-5 w-5" />;
      case "clip_reward":
        return <Youtube className="h-5 w-5" />;
      default:
        return <Sparkles className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    const statusConfig = {
      completed: { variant: "default" as const, color: "bg-accent" },
      pending: { variant: "secondary" as const, color: "bg-yellow-500" },
      failed: { variant: "destructive" as const, color: "bg-destructive" },
      processing: { variant: "secondary" as const, color: "bg-blue-500" },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return config ? (
      <Badge variant={config.variant} className="text-xs">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    ) : null;
  };

  const displayAmount = redemption ? redemption.reward : amount;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
      className="flex items-center justify-between p-4 rounded-lg bg-background/30 border border-border transition-all duration-200"
      data-testid="transaction-item"
    >
      <div className="flex items-center space-x-4">
        <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
          <div className="text-lg" data-testid="transaction-icon">
            {getTypeIcon(type)}
          </div>
        </div>
        <div>
          <div className="font-medium" data-testid="transaction-description">
            {description}
          </div>
          <div className="flex items-center space-x-2">
            <div
              className="text-sm text-muted-foreground"
              data-testid="transaction-time"
            >
              {formatDistanceToNow(createdAt, { addSuffix: true })}
            </div>
            {getStatusBadge(status)}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div
          className={`font-semibold ${getTypeColor(type)}`}
          data-testid="transaction-amount"
        >
          {displayAmount > 0 ? "+" : ""}
          {displayAmount} tokens
        </div>
      </div>
    </motion.div>
  );
};

export default TransactionItem;
